import axios from 'axios';
import mockGeminiService from './mockGeminiService';

// Backend API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const USE_MOCK_AI = true; // Set to true for portfolio demo without API costs

// Helper function to get auth token
const getAuthToken = (): string | null => {
  return localStorage.getItem('token');
};

// Helper function to create auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Generate a legal document based on a prompt
 * @param prompt The document requirements or description
 * @returns The generated legal document
 */
export const generateLegalDocument = async (prompt: string): Promise<string> => {
  // Use mock service for portfolio demo (no API costs!)
  if (USE_MOCK_AI) {
    console.log('✨ Using mock AI service for demo - generating realistic contract...');
    return await mockGeminiService.generateLegalDocument(prompt);
  }
  
  try {
    console.log('Generating legal document via backend API');
    
    const response = await axios.post(
      `${API_BASE_URL}/gemini/generate`,
      { prompt },
      {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        timeout: 60000, // 60 second timeout
      }
    );

    if (response.data.success) {
      return response.data.document;
    } else {
      throw new Error(response.data.message || 'Failed to generate document');
    }
  } catch (error: any) {
    console.error('Error in generateLegalDocument:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Analyze a legal document and provide insights
 * @param documentText The text of the document to analyze
 * @returns Analysis and insights about the document
 */
export const analyzeLegalDocument = async (documentText: string): Promise<string> => {
  try {
    console.log('Analyzing legal document via backend API');
    
    const response = await axios.post(
      `${API_BASE_URL}/gemini/analyze`,
      { documentText },
      {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        timeout: 60000,
      }
    );

    if (response.data.success) {
      return response.data.analysis;
    } else {
      throw new Error(response.data.message || 'Failed to analyze document');
    }
  } catch (error: any) {
    console.error('Error in analyzeLegalDocument:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Simplify legal language to make it more understandable
 * @param legalText The legal text to simplify
 * @returns Simplified version of the legal text
 */
export const simplifyLegalLanguage = async (legalText: string): Promise<string> => {
  try {
    console.log('Simplifying legal text via backend API');
    
    const response = await axios.post(
      `${API_BASE_URL}/gemini/simplify`,
      { legalText },
      {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        timeout: 60000,
      }
    );

    if (response.data.success) {
      return response.data.simplifiedText;
    } else {
      throw new Error(response.data.message || 'Failed to simplify text');
    }
  } catch (error: any) {
    console.error('Error in simplifyLegalLanguage:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Improve legal text based on user request
 * @param legalText The original legal text to improve
 * @param request The user's request for improvement
 * @returns Improved version of the legal text
 */
export const improveLegalText = async (legalText: string, request: string): Promise<string> => {
  try {
    console.log('Improving legal text via backend API');
    
    const response = await axios.post(
      `${API_BASE_URL}/gemini/improve`,
      { legalText, request },
      {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        timeout: 60000,
      }
    );

    if (response.data.success) {
      return response.data.improvedText;
    } else {
      throw new Error(response.data.message || 'Failed to improve text');
    }
  } catch (error: any) {
    console.error('Error in improveLegalText:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

const geminiApi = {
  generateLegalDocument,
  analyzeLegalDocument,
  simplifyLegalLanguage,
  improveLegalText,
};

export default geminiApi;
